import React from "react"
import { useSelector } from "react-redux"
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  createStyles,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from "@material-ui/core"

import { RootState, useAppDispatch } from "@root/store"
import { selectDebugInfo } from "@root/store/selectors"
import {
  clearEverything,
  disablePrivacyManagerFeatures,
  enablePrivacyManagerFeatures,
  loadDebugInfo,
  resetContact,
  setDataCollectionEnabled,
  toggleIaaDisplayPaused,
} from "@root/store/thunks"
import { PrivacyManagerFeature } from "@ua/web-sdk/lib/privacy-manager/types"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {},
    title: { paddingBottom: theme.spacing(1) },
    dangerButton: { backgroundColor: "rgb(244, 67, 54)" },
    card: {
      marginBlockStart: 10,
      marginBlockEnd: 10,
      padding: theme.spacing(1),
    },
  })
)

// this just exists to give us a type-enforced listing of all available privacy
// manager features; if you upgrade the web sdk version and this blows up, it's
// because we've added a new one!
const _PrivacyManagerFeatures: { [k in PrivacyManagerFeature]: true } = {
  analytics: true,
  contacts: true,
  feature_flags: true,
  in_app_automation: true,
  push: true,
  tags_and_attributes: true,
}
const PrivacyManagerFeatures = Object.keys(
  _PrivacyManagerFeatures
) as readonly PrivacyManagerFeature[]

const Debug: React.FC = () => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const debug = useSelector((state: RootState) => state.debug)
  const metadata = useSelector((state: RootState) => state.metadata)
  const debugInfo = useSelector(selectDebugInfo)
  const [reload, setReload] = React.useState(0)
  const [selectedPush, setSelectedPush] = React.useState(
    debugInfo?.pushHistory.length ? debugInfo.pushHistory.length - 1 : 0
  )
  const [features, setFeatures] = React.useState<PrivacyManagerFeature[]>([])

  const addFeature = React.useCallback(
    (feature: PrivacyManagerFeature) => {
      setFeatures([...features, feature])
    },
    [features]
  )
  const removeFeature = React.useCallback(
    (feature: PrivacyManagerFeature) => {
      setFeatures(features.filter((f) => f !== feature))
    },
    [features]
  )

  const toggleIaaPausedState = React.useCallback(
    () => dispatch(toggleIaaDisplayPaused()),
    [dispatch]
  )

  const enableDataCollection = React.useCallback(
    () => dispatch(setDataCollectionEnabled(true)),
    [dispatch]
  )
  const disableDataCollection = React.useCallback(
    () => dispatch(setDataCollectionEnabled(false)),
    [dispatch]
  )

  const enableFeatures = React.useCallback(() => {
    dispatch(enablePrivacyManagerFeatures(features))
    setFeatures([])
  }, [dispatch, features, setFeatures])

  const disableFeatures = React.useCallback(() => {
    dispatch(disablePrivacyManagerFeatures(features))
    setFeatures([])
  }, [dispatch, features, setFeatures])

  React.useEffect(() => {
    dispatch(loadDebugInfo())
  }, [dispatch, reload])

  if (!debugInfo) {
    return (
      <Typography variant="h4">
        {debug.status !== "pending" && (
          <Button onClick={() => setReload(reload + 1)}>Reload</Button>
        )}
        {getStateMessage()}
      </Typography>
    )
  }

  return (
    <Grid container justifyContent="flex-start" alignItems="flex-start">
      <Grid item sm={8}>
        <Grid container justifyContent="flex-start" alignItems="center">
          <Grid item sm={12}>
            <Typography variant="h6" className={classes.title}>
              Channel
            </Typography>
            <List className={classes.list}>
              <ListItem>
                <ListItemText
                  primary="Channel ID"
                  secondary={debugInfo.channelId ?? "none"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Push Status"
                  secondary={debugInfo.pushStatus}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Grid item sm={12}>
          <Typography variant="h6" className={classes.title}>
            Browser
          </Typography>
          <List className={classes.list}>
            <ListItem>
              <ListItemText
                primary="Push Supported?"
                secondary={debugInfo.supported ? "True" : "False"}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Notification Permission"
                secondary={debugInfo.notificationPermission}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid item sm={12}>
          <Typography variant="h6">Privacy Manager Features</Typography>
          <List className={classes.list}>
            <ListItem>
              <ListItemText
                primary="Enabled Features"
                secondary={debugInfo.privacyManagerFeatures.join(", ")}
              />
            </ListItem>
            <ListItem>
              <List>
                {PrivacyManagerFeatures.map((feature) => (
                  <ListItem key={feature}>
                    <Checkbox
                      checked={features.includes(feature)}
                      onChange={(ev) =>
                        ev.target.checked
                          ? addFeature(feature)
                          : removeFeature(feature)
                      }
                    />
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </ListItem>
            <ListItem>
              <ButtonGroup>
                <Button onClick={enableFeatures}>Enable Features</Button>
                <Button onClick={disableFeatures}>Disable Features</Button>
              </ButtonGroup>
            </ListItem>
          </List>
        </Grid>
        <Grid item sm={12}>
          <Typography variant="h6" className={classes.title}>
            SDK
          </Typography>
          <List className={classes.list}>
            <ListItem>
              <ListItemText
                primary="Data Collection Enabled?"
                secondary={
                  metadata.supportInfo?.dataCollectionEnabled ? "True" : "False"
                }
              />
            </ListItem>
            <ListItem>
              {metadata.supportInfo?.dataCollectionEnabled ? (
                <Button
                  variant="outlined"
                  className={classes.dangerButton}
                  onClick={disableDataCollection}
                >
                  Disable Data Collection
                </Button>
              ) : (
                <Button variant="outlined" onClick={enableDataCollection}>
                  Enable Data Collection
                </Button>
              )}
            </ListItem>
          </List>
        </Grid>
        <Grid item sm={12}>
          <Typography variant="h6" className={classes.title}>
            Contact
          </Typography>
          <List className={classes.list}>
            <ListItem>
              <ListItemText
                primary="Contact ID"
                secondary={debugInfo.contactId}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Anonymous?"
                secondary={debugInfo.contactIsAnonymous ? "True" : "False"}
              />
            </ListItem>
          </List>
        </Grid>

        <Grid item sm={12}>
          <Typography variant="h6" className={classes.title}>
            Conflict Events
          </Typography>
          <List className={classes.list}>
            {metadata.conflictEvents?.map(({ occurred, event }) => (
              <ListItem key={`${occurred}`}>
                <ListItemText
                  primary={`Event: ${new Date(occurred).toISOString()}`}
                  secondary={<pre>{JSON.stringify(event, null, 2)}</pre>}
                />
              </ListItem>
            ))}
            {!metadata.conflictEvents?.length && (
              <ListItem>
                <ListItemText primary="No Events" />
              </ListItem>
            )}
          </List>
        </Grid>

        <Grid item sm={12}>
          <Typography variant="h6" className={classes.title}>
            Push History
          </Typography>
          {debugInfo.pushHistory.length > 0 && (
            <List className={classes.list}>
              <ListItem>
                <ButtonGroup variant="contained">
                  <Button
                    disabled={selectedPush === 0}
                    onClick={() => setSelectedPush(selectedPush - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={selectedPush === debugInfo.pushHistory.length - 1}
                    onClick={() => setSelectedPush(selectedPush + 1)}
                  >
                    Next
                  </Button>
                </ButtonGroup>
              </ListItem>
              <ListItem>
                <Paper>
                  <pre>
                    {JSON.stringify(
                      debugInfo.pushHistory[selectedPush] ?? {},
                      null,
                      2
                    )}
                  </pre>
                </Paper>
              </ListItem>
            </List>
          )}
        </Grid>
      </Grid>

      <Grid item sm={4}>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6">Reset user data</Typography>
            <Typography>
              Opt the channel out of web push.{" "}
              <Link
                href="https://api-spec-dot-ua-web-ci.appspot.com/namedusers.html?highlight=reset#post-POST%20-api-contacts-reset"
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
              >
                Reset the contact
              </Link>
              . Delete all local data.
            </Typography>
            <Button
              variant="outlined"
              className={classes.dangerButton}
              onClick={() => {
                dispatch(clearEverything())
                dispatch(resetContact())
              }}
            >
              Reset
            </Button>
          </CardContent>
        </Card>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6">In App Experience</Typography>
            <Typography>
              Display Status:{" "}
              {debugInfo.iaaDisplayPausedState ? "Paused" : "Running"}
            </Typography>
            <Button
              data-qa-selector="toggle"
              type="button"
              variant="contained"
              onClick={(ev) => {
                ev.preventDefault()
                toggleIaaPausedState()
              }}
              disabled={!debugInfo.channelId}
            >
              {debugInfo.iaaDisplayPausedState
                ? "Resume Display"
                : "Pause Display"}
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )

  function getStateMessage(): string {
    switch (debug.status) {
      case "idle":
      case "pending":
        return "Loading…"
      case "fulfilled":
        return "Error: Should be populated. Try and refresh maybe?"
      case "rejected":
        return `Unable to fetch debug info: ${debug.error ?? "unknown"}`
      default:
        const msg: never = debug.status
        throw new Error(`invalid state status: ${msg}`)
    }
  }
}

export default Debug
